import { useEffect, useState } from 'react';
import {
  Box,
  Dialog as MDialog,
  DialogContent,
  IconButton,
  Typography,
  DialogActions,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import FHButton from '../FHButton';
import FHCheckboxListItem from '../FHCheckboxListItem';
import { getThemeColor } from '../../../utils/common';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../utils/colors';
import { Themes } from '../../../constants';
import {
  GoalTypes,
  GoalTypeOptions,
} from '../../layout/treatment-plan/goals/common/constants/general';
import CloseIcon from '../../svgs/CloseIcon';

import type { DialogProps as MDialogProps } from '@mui/material';
import type { ReactNode } from 'react';

interface IDialogListOption {
  id: number;
  label?: string;
  checked: boolean;
  dataTestId?: string;
  goalType?: GoalTypes;
  contentRenderer?: (id: number | string, label: string | undefined, checked: boolean) => ReactNode;
}

interface FHItemSelectDialogProps extends MDialogProps {
  title: string;
  submitText?: string;
  cancelText?: string;
  colorTheme?: Themes;
  width?: number | string;
  height?: number | string;
  itemList: Array<IDialogListOption>;
  submitButtonStyles?: object;
  onSave: (selectedItems: number[]) => void;
  onClose: () => void;
  showGoalTypeOptions?: boolean; // New prop to control the display of BRG and SAG options
}

interface FStyledDialogsProps extends MDialogProps {
  width?: number | string;
  height?: number | string;
}

function areItemListsEquivalent(
  currentList: Array<IDialogListOption>,
  nextList: Array<IDialogListOption>,
) {
  if (currentList.length !== nextList.length) return false;

  return currentList.every((currentItem, index) => {
    const nextItem = nextList[index];
    return (
      currentItem.id === nextItem.id &&
      currentItem.label === nextItem.label &&
      currentItem.checked === nextItem.checked &&
      currentItem.dataTestId === nextItem.dataTestId &&
      currentItem.goalType === nextItem.goalType
    );
  });
}

const StyledDialog = styled(MDialog, {
  shouldForwardProp: (prop) => prop !== 'colorTheme',
})<FStyledDialogsProps>(({ width, height }: any) => ({
  '& .MuiDialog-paper': {
    boxSizing: 'border-box',
    width: width ? width : '50vw',
    height: height ? height : 'auto',
    maxWidth: '100vw',
    maxHeight: '70vh',
    background: FORTA_WHITE,
    boxShadow: `0px 4px 4px rgba(0, 0, 0, 0.3)`,
    borderRadius: '10px',
    padding: '16px',
    '@media (max-width: 600px)': {
      padding: '4px',
    },
    border: `2px solid ${FORTA_BLUE.light3}`,
    margin: 0,
  },
  '& .MuiDialogContent-root': {
    maxWidth: '100vw',
    maxHeight: '60vh',
    padding: '0 16px 0 16px',
  },
  '& .MuiModal-backdrop': {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
}));

function FHItemSelectDialog(props: FHItemSelectDialogProps) {
  const {
    open,
    title,
    submitText = 'Submit',
    cancelText = 'Cancel',
    colorTheme = Themes.Primary,
    itemList = [],
    width,
    height,
    submitButtonStyles = {},
    onSave,
    onClose,
    showGoalTypeOptions = false, // Default to false if not provided
  } = props;

  const [listItemData, setListItemData] = useState<Array<IDialogListOption>>(itemList);
  const [isAllSelected, setIsAllSelected] = useState(false);

  useEffect(() => {
    if (!open) return;

    setListItemData((currentList) =>
      areItemListsEquivalent(currentList, itemList) ? currentList : itemList,
    );
    setIsAllSelected((currentValue) => {
      const nextValue = itemList.length > 0 && itemList.every((item) => item.checked);
      return currentValue === nextValue ? currentValue : nextValue;
    });
  }, [itemList, open]);

  const updateListItems = (updateFn: (item: IDialogListOption) => IDialogListOption) => {
    const updatedList = listItemData.map(updateFn);
    setListItemData(updatedList);
    setIsAllSelected(updatedList.every((item) => item.checked));
  };

  const handleListItemChange = (id: number | string, checked: boolean) => {
    updateListItems((item) => (item.id === id ? { ...item, checked } : item));
  };

  const handleSelectByType = (type: GoalTypes | GoalTypeOptions) => {
    updateListItems((item) => ({
      ...item,
      checked: type === GoalTypeOptions.All || item.goalType === type || item.checked,
    }));
  };

  const handleDeselectByType = (type: GoalTypes | GoalTypeOptions) => {
    updateListItems((item) => ({
      ...item,
      checked: type !== GoalTypeOptions.All && item.goalType !== type && item.checked,
    }));
  };

  const isAllOfTypeSelected = (type: GoalTypes) => {
    return listItemData.every((item) => item.goalType !== type || item.checked);
  };

  const handleSave = () => {
    const selectedItems = listItemData.filter((item) => item.checked).map((item) => item.id);
    onSave(selectedItems);
  };

  return (
    <StyledDialog width={width} height={height} open={open}>
      <Box
        sx={{
          p: {
            xs: 1,
            sm: 2,
          },
        }}
      >
        <Typography variant="h4" fontWeight={500} color={getThemeColor(colorTheme).primary}>
          {title}
        </Typography>
        <IconButton
          sx={{ p: 0, position: 'absolute', top: 24, right: 24 }}
          disableRipple
          onClick={onClose}
        >
          <CloseIcon color={getThemeColor(colorTheme).primary} />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 3,
            flexWrap: 'wrap',
            my: { xs: 1, sm: 0 },
          }}
        >
          {showGoalTypeOptions && (
            <>
              <Link
                data-testid={'fhItemSelectBRGLink'}
                color={getThemeColor(colorTheme).primary}
                fontSize={16}
                onClick={() =>
                  isAllOfTypeSelected(GoalTypes.BehaviorReduction)
                    ? handleDeselectByType(GoalTypeOptions.BehaviorReduction)
                    : handleSelectByType(GoalTypeOptions.BehaviorReduction)
                }
              >
                {isAllOfTypeSelected(GoalTypes.BehaviorReduction)
                  ? 'Deselect all BRG'
                  : 'Select all BRG'}
              </Link>
              <Link
                data-testid={'fhItemSelectSAGLink'}
                color={getThemeColor(colorTheme).primary}
                fontSize={16}
                onClick={() =>
                  isAllOfTypeSelected(GoalTypes.SkillAcquisition)
                    ? handleDeselectByType(GoalTypeOptions.SkillAcquisition)
                    : handleSelectByType(GoalTypeOptions.SkillAcquisition)
                }
              >
                {isAllOfTypeSelected(GoalTypes.SkillAcquisition)
                  ? 'Deselect all SAG'
                  : 'Select all SAG'}
              </Link>
            </>
          )}
          <Link
            data-testid={'fhItemSelectAllLink'}
            color={getThemeColor(colorTheme).primary}
            fontSize={16}
            onClick={() =>
              isAllSelected
                ? handleDeselectByType(GoalTypeOptions.All)
                : handleSelectByType(GoalTypeOptions.All)
            }
          >
            {isAllSelected ? 'Deselect all' : 'Select all'}
          </Link>
        </Box>
      </Box>
      <DialogContent>
        {listItemData.map((item: IDialogListOption) => (
          <FHCheckboxListItem
            key={item.id}
            id={item.id}
            label={item.label}
            checked={item.checked}
            colorTheme={colorTheme}
            contentRenderer={item.contentRenderer}
            onChange={handleListItemChange}
            dataTestId={item.dataTestId}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{ py: 1 }}>
        <FHButton
          onClick={onClose}
          sx={{
            mr: 2,
            backgroundColor: FORTA_WHITE,
            color: FORTA_GRAY4,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: FORTA_BLUE.light2,
            ':hover': {
              backgroundColor: FORTA_WHITE,
              color: FORTA_GRAY4,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: FORTA_BLUE.light2,
            },
          }}
          colorTheme={colorTheme}
        >
          {cancelText}
        </FHButton>
        <FHButton
          onClick={handleSave}
          sx={{ ...submitButtonStyles }}
          colorTheme={colorTheme}
          data-testid={'fhItemSelectSubmit'}
        >
          {submitText}
        </FHButton>
      </DialogActions>
    </StyledDialog>
  );
}

export default FHItemSelectDialog;
